import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, MapPin, Phone, Utensils, Truck, Users, Heart, Award } from 'lucide-react';
import { restaurantInfo, featuredDishes, reviews, services } from '../data/mock';

const HomePage = () => {
  const displayReviews = reviews.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="hero-content">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 fade-in">
                Authentic African & <br />
                <span className="text-red-600">Antillean Cuisine</span>
              </h1>
              <div className="accent-line mb-6"></div>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed slide-up">
                Experience the rich flavors of traditional African and Antillean dishes 
                at Imani Food. From Senegalese Thiep to Congolese Pondu, 
                every dish tells a story of heritage and passion.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-start items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
                <div className="flex items-center space-x-2 text-gray-900">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={20} fill="#eab308" className="text-yellow-500" />
                    ))}
                  </div>
                  <span className="font-semibold">{restaurantInfo.rating} ({restaurantInfo.totalReviews} reviews)</span>
                </div>
                <div className="text-gray-400">•</div>
                <div className="text-gray-700 font-medium">{restaurantInfo.priceRange}</div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
                <Link to="/menu" className="btn-primary">
                  View Our Menu
                </Link>
                <button className="btn-secondary" onClick={() => window.open('https://www.ubereats.com', '_blank')}>
                  Order on Uber Eats
                </button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="image-rounded">
                <img 
                  src="https://images.unsplash.com/photo-1575950674322-3a1977724f2e?w=800&h=600&fit=crop" 
                  alt="Authentic Caribbean grilled fish with rice - Traditional Antillean cuisine"
                  className="image-hero"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Heart size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Made with Love</p>
                    <p className="text-sm text-gray-600">Traditional recipes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <MapPin size={32} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Visit Our Location</h3>
              <p className="text-gray-600 mb-2">
                {restaurantInfo.address}
              </p>
              <p className="text-sm text-gray-500">
                Wheelchair accessible with parking
              </p>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Clock size={32} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Open Daily</h3>
              <p className="text-gray-600 font-medium mb-2">
                12:00 PM - 10:00 PM
              </p>
              <p className="text-sm text-gray-500">
                Extended hours on weekends
              </p>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <Phone size={32} className="text-orange-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Call Us</h3>
              <p className="text-gray-600 font-medium mb-2">
                {restaurantInfo.phone}
              </p>
              <p className="text-sm text-gray-500">
                Reservations & takeaway
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Dishes */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Most Popular Dishes
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our customers' favorite authentic dishes, 
              each prepared with traditional recipes and the finest ingredients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Dish 1 */}
            <div className="card-custom group">
              <div className="relative mb-6">
                <span className="popular-badge absolute top-4 right-4 z-10">
                  #1 most liked
                </span>
                <img 
                  src="https://images.unsplash.com/photo-1604329756574-bda1f2cada6f?w=400&h=300&fit=crop" 
                  alt="Traditional African rice dish with vegetables and meat"
                  className="image-card group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {featuredDishes[0].name}
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {featuredDishes[0].description}
              </p>
              <div className="flex justify-between items-center">
                <span className="price-badge">
                  CHF {featuredDishes[0].price}
                </span>
                <Link to="/menu" className="text-red-600 hover:text-red-700 font-medium text-sm">
                  Order Now →
                </Link>
              </div>
            </div>

            {/* Dish 2 */}
            <div className="card-custom group">
              <div className="relative mb-6">
                <span className="popular-badge absolute top-4 right-4 z-10">
                  #2 most liked
                </span>
                <img 
                  src="https://images.unsplash.com/photo-1632852576480-c10a8e19496a?w=400&h=300&fit=crop" 
                  alt="Caribbean brown-stew fish with rice and peas"
                  className="image-card group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {featuredDishes[1].name}
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {featuredDishes[1].description}
              </p>
              <div className="flex justify-between items-center">
                <span className="price-badge">
                  CHF {featuredDishes[1].price}
                </span>
                <Link to="/menu" className="text-red-600 hover:text-red-700 font-medium text-sm">
                  Order Now →
                </Link>
              </div>
            </div>

            {/* Dish 3 */}
            <div className="card-custom group">
              <div className="relative mb-6">
                <span className="popular-badge absolute top-4 right-4 z-10">
                  #3 most liked
                </span>
                <img 
                  src="https://images.unsplash.com/photo-1632859965308-d15227508c4b?w=400&h=300&fit=crop" 
                  alt="Jamaican ackee and saltfish with dumplings"
                  className="image-card group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {featuredDishes[2].name}
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {featuredDishes[2].description}
              </p>
              <div className="flex justify-between items-center">
                <span className="price-badge">
                  CHF {featuredDishes[2].price}
                </span>
                <Link to="/menu" className="text-red-600 hover:text-red-700 font-medium text-sm">
                  Order Now →
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/menu" className="btn-primary">
              See Full Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="image-rounded">
              <img 
                src="https://images.unsplash.com/photo-1636447155412-d57350005073?w=600&h=400&fit=crop" 
                alt="Chef preparing authentic African cuisine at grill"
                className="image-hero"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="accent-line mb-6" style={{ margin: '0' }}></div>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Imani Food was born from a simple dream: to share the authentic, 
                  soul-warming flavors of African and Antillean cuisine with the 
                  beautiful community of Meyrin and beyond.
                </p>
                <p>
                  Our name "Imani" means "faith" in Swahili, reflecting our faith 
                  in the power of traditional recipes, quality ingredients, and the 
                  joy that comes from sharing a meal prepared with love.
                </p>
                <p>
                  What makes us special is not just our authentic recipes, but our 
                  commitment to giving back. A portion of our profits supports 
                  humanitarian projects, making every meal you enjoy with us 
                  contribute to positive change.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/about" className="btn-secondary">
                  Learn More About Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How We Serve You
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600">
              Multiple convenient options to enjoy our authentic cuisine
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Utensils size={40} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Dine-In Experience</h3>
              <p className="text-gray-600 mb-6">
                Enjoy our warm atmosphere with wheelchair-accessible seating. 
                Perfect for families, groups, and solo dining.
              </p>
              <Link to="/contact" className="btn-secondary">
                Make Reservation
              </Link>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Truck size={40} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Delivery & Takeaway</h3>
              <p className="text-gray-600 mb-6">
                Fast delivery through Uber Eats and Just Eat, 
                or call us directly for takeaway orders.
              </p>
              <button 
                className="btn-accent"
                onClick={() => window.open('https://www.ubereats.com', '_blank')}
              >
                Order Online
              </button>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <Users size={40} className="text-orange-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Catering Services</h3>
              <p className="text-gray-600 mb-6">
                Large group orders welcome! We cater events 
                and provide generous portions for any occasion.
              </p>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600">
              Authentic flavors that bring back memories and create new ones
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayReviews.map((review, index) => (
              <div key={index} className="card-custom">
                <div className="flex items-center justify-between mb-6">
                  <div className="stars">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.timeAgo}</span>
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{review.text}"
                </p>
                <div className="text-gray-900 font-semibold">
                  – {review.name}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/about" className="btn-secondary">
              Read All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="card-custom text-center max-w-4xl mx-auto" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)', border: '1px solid #fecaca' }}>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                <Award size={32} className="text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ready to Experience Authentic African Cuisine?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join us for an unforgettable culinary journey through the rich flavors of Africa and the Antilles.
              Every dish is prepared with love and traditional techniques passed down through generations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/menu" className="btn-primary">
                Explore Menu
              </Link>
              <Link to="/contact" className="btn-secondary">
                Make Reservation
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;